import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors } from '../../styles/theme/colors';
import { DecimalNumberInput } from '../../components/ui';
import type { SafetyStockRow } from '../../api/services/SafetyStockService';
import { SafetyStockService } from '../../api/services/SafetyStockService';
import { dialogZoomPaperSx } from '../../components/ui/DialogZoomTransition';

export interface EditSafetyStockModalProps {
  open: boolean;
  onClose: () => void;
  row: SafetyStockRow | null;
  onSuccess: () => void;
}

const EditSafetyStockModal: React.FC<EditSafetyStockModalProps> = ({
  open,
  onClose,
  row,
  onSuccess,
}) => {
  const [safetyStock, setSafetyStock] = useState<string>(
    row?.stockSeguridad != null ? String(row.stockSeguridad) : '',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (row) {
      setSafetyStock(row.stockSeguridad != null ? String(row.stockSeguridad) : '');
      setError(null);
      setConfirmed(false);
    }
  }, [row]);

  const handleClose = () => {
    setError(null);
    setConfirmed(false);
    onClose();
  };

  const handleSave = async () => {
    if (!row) return;
    const value = safetyStock.trim() === '' ? null : parseInt(safetyStock, 10);
    if (value !== null && (isNaN(value) || value < 0)) {
      setError('El valor debe ser un número entero mayor o igual a 0.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await SafetyStockService.productLocationSafetyStockControllerUpsert(
        row.productId,
        row.locationId,
        { safetyStock: value },
      );
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar el stock de seguridad.');
    } finally {
      setLoading(false);
    }
  };

  if (!row) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: dialogZoomPaperSx({
          borderRadius: 2,
          // Sin overflow:hidden: evita recortes de labels/acciones cuando el ancho es justo.
          overflow: 'visible',
          boxShadow: '0 20px 60px rgba(2, 6, 23, 0.18)',
          margin: 'auto',
          width: '100%',
          maxWidth: { xs: 'min(600px, calc(100% - 32px))', sm: '600px' },
          bgcolor: 'background.paper',
        }),
      }}
    >
      <DialogTitle
        sx={(theme) => ({
          padding: '14px 20px',
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          fontSize: '1rem',
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            minWidth: 0,
            justifyContent: 'flex-start',
          }}
        >
          <EditIcon sx={{ fontSize: 22, flexShrink: 0 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#ffffff',
              fontSize: '1rem',
              overflow: 'visible',
              textOverflow: 'clip',
            }}
          >
            Editar stock
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: '#ffffff',
            '&:hover': { color: alpha('#fff', 0.85) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={(theme) => ({
          padding: '48px 28px 32px',
          overflowY: 'auto',
          overflowX: 'visible',
          minHeight: 120,
          backgroundColor: theme.palette.background.paper,
        })}
      >
        {error && (
          <Alert
            severity="error"
            sx={(theme) => ({
              mb: 2,
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor:
                theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.15) : '#fee2e2',
              color: theme.palette.mode === 'dark' ? theme.palette.error.light : '#dc2626',
              border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.35) : '#fecaca'}`,
              '& .MuiAlert-icon': {
                color: theme.palette.mode === 'dark' ? theme.palette.error.light : '#dc2626',
              },
            })}
          >
            {error}
          </Alert>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            mt: 1,
            width: '100%',
            minWidth: 0,
            '& .MuiTextField-root': {
              maxWidth: '100%',
            },
            '& .MuiInputLabel-root': {
              whiteSpace: 'normal',
              lineHeight: 1.3,
            },
            '& .MuiInputLabel-root.MuiInputLabel-shrink': {
              textOverflow: 'clip',
              overflow: 'visible',
            },
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Producto
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {row.productName} ({row.productCode})
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Ubicación
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {row.sucursalName}
            </Typography>
          </Box>
          <DecimalNumberInput
            fullWidth
            variant="outlined"
            size="medium"
            label="Stock de seguridad *"
            value={safetyStock}
            onChange={(val) => setSafetyStock(val != null ? String(Math.trunc(val)) : '')}
            placeholder="0"
            decimalPlaces={0}
            min={0}
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={loading}
                sx={{
                  color: colors.success.main,
                  '&.Mui-checked': { color: colors.success.main },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                Confirmar actualización
              </Typography>
            }
            sx={{ marginLeft: 0 }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={(theme) => ({
          padding: '16px 24px',
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          gap: 1.5,
          rowGap: 1.5,
          overflow: 'visible',
        })}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={(theme) => ({
            minWidth: '120px',
            padding: '9px 18px',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            color: theme.palette.text.secondary,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.06)
                : theme.palette.grey[50],
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
          })}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !confirmed}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={(theme) => ({
            minWidth: '120px',
            padding: '9px 18px',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
            color: '#ffffff',
            boxShadow: 'none',
            '&:hover': {
              background: 'linear-gradient(to right, #0891b2, #059669)',
              boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
            },
            '&:disabled': {
              background: colors.grey[400],
              color: '#ffffff',
              opacity: theme.palette.mode === 'dark' ? 0.5 : 0.6,
              cursor: 'not-allowed',
              boxShadow: 'none',
            },
          })}
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSafetyStockModal;
